import comm from "csi/comm";
import PostRequest from "csi/comm/PostRequest";
import html from "csi/html";
import locale from "csi/locale";
import session from "csi/session";
import New from "csi/table/New";
import MessageDialog from "csi/widget/MessageDialog";
import xml from "csi/xml";
import Search from "csi/xml/Search";
import CheckBox from "csi/widget/CheckBox";
import domConstruct from "dojo/dom-construct";
import declare from "dojo/_base/declare";
import { sessionStore } from "csi/state/rootStore";
import Writer from "csi/xml/Writer";
import { submitWriter } from "csi/service/writerService";

const NewCompanyCustomFont = declare(New, {

	/** @property {FontFileSrcMap} fileSrcList - Each family name corresponds to a Map which has key (file name) and value (file vault url) */
	fileSrcList: null,

	/** @property {Record<string,string>} fileUrlList - List of file vault urls */
	fileUrlList: null,

	/** @property {string[]} newNodeUrls - list of new node urls */
	newNodeUrls: null,

	/**
	* @override
	* do not use a confirm icon
	*/
	getConfirmIcon: function () {
		return null;
	},

	/**
	* @override
	* Require license agreement if user hasn't agreed already
	*/
	hasToConfirmAction: function () {
		return !sessionStore.userDetails.HasAcceptedCustomFontDialog;
	},

	/**
	* @override
	* Display user license agreement
	*/
	confirmAction: function (params) {
		params = {
			destroyOnHide: true,
			buttons: [MessageDialog.BUTTON_AGREE + MessageDialog.BUTTON_PRIMARY, MessageDialog.BUTTON_CANCEL],
			bInitiallyDisableConfirmButton: true,
			message: locale.resource("CustomFontUploadDialogContent"),
			title: locale.resource("CustomFontUploadDialogTitle"),

			onMessage: function (messageNode): void {
				const self = this;
				let hasAcceptedCheckbox = false;
				const checkBox = new CheckBox({
					value: false,
					name: "checkBox",
					checked: false,
					onChange: (): void => {
						hasAcceptedCheckbox = !hasAcceptedCheckbox;
						html.setDisabled(self.confirmButton, !hasAcceptedCheckbox);
					},
				});

				const acceptMessage = locale.resource("CustomFontUploadDialogAgreement");

				const checkBoxArea = domConstruct.create("div", { class: "csi-agreement-checkbox-area" }, messageNode, "after");

				const checkBoxSpan = domConstruct.create("span", { class: "csi-agreement-checkbox" });
				checkBoxArea.appendChild(checkBoxSpan);
				checkBox.placeAt(checkBoxSpan);

				html.create("span", { innerText: acceptMessage }, checkBoxSpan, "after");
			},
		};
		this.inherited(arguments);
	},

	onConfirm: function (key) {
		if (key === MessageDialog.BUTTON_AGREE) {
			const writer = new Writer();

			writer.changeNode(sessionStore.userDetailsUrl)
				.changeAttribute("HasAcceptedCustomFontDialog", xml.ATTR_BOOLEAN, true);

			sessionStore.submitWriter(writer);

			this.loadFileList().then(() => {
				this.process();
			});
		} else {
			this.postProcess();
		}
	},

	/**
	 * Select font from file browser and load it into web browser.
	 */
	async loadFileList() {
		this.fileUrlList = {};
		// open select file dialog
		this.fileSrcList = await html.selectFiles(".ttf,.woff,.otf");
		this.displayProgressDialog({
			closable: false,
			message: locale.resource("Action_ProgressWait"),
			progressValue: 0,
		});
		const loadRatio = 0.75 / this.fileSrcList.length;
		for (const [index, file] of this.fileSrcList.entries()) {
			const response = await comm.publishFile(file.name, null, file);
			this.displayProgressValue(index * loadRatio);
			this.fileUrlList[file.name] = response.getFileUrl();
		}
	},

	/**
	 * Load font previews, try to display style: normal / weight: 400 / stretch: 100%.
	 */
	async loadFontPreviews() {
		try {
			await new PostRequest("SiteAdmin", "ParseFont").go();
		} catch (err) {
			// usually the c8-action handles deletion, but if it fails, need to cleanup manually
			const { m_writer, newNodeUrls } = this;
			console.error(err);
			for (const url of newNodeUrls) {
				m_writer.deleteNode(url);
			}
			await submitWriter(m_writer);
			this.displayErrorDialog({ message: locale.resource("ParseFontError") });
			return;
		}

		const data = await new Search().byType("FontFamily")
			.getRequest()
			.go();
		const nodes = data.getResultNodes();
		for (const node of nodes) {
			for (const file in node.FileMap) {
				const fileVaultUrl = node.FileMap[file];
				const requestUrl = comm.getFileHref(fileVaultUrl);
				const descriptors = {
					style: "normal",
					weight: "400",
					stretch: "100%",
				};
				const font = new FontFace(node.$Name, `url(${requestUrl})`, descriptors);
				await font.load();
				document.fonts.add(font);
			}
		}
	},

	/**
	 * @override
	 * Select files to upload.
	 */
	onPreActionDeferreds(dfds) {
		if (!this.hasToConfirmAction()) {
			dfds.push(this.loadFileList());
		}
	},

	/**
	 * @override
	 * Refresh the table after the font preview files load.
	 */
	postProcessSuccess() {
		this.loadFontPreviews().then(() => {
			this.widget.refreshData();
			this.displayProgressValue(1);
			this.inherited(arguments);
		});
	},

	/**
	 * @override
	 * Process the font families and files.
	 */
	toWriter() {
		this.newNodeUrls = [];

		const {
			fileSrcList,
			fileUrlList,
			m_typeNode,
			m_writer,
			newNodeUrls,
		} = this;

		for (const file of fileSrcList) {
			const newUrl = comm.createUrl();
			newNodeUrls.push(newUrl);
			const { name } = file;
			m_writer.createNode(newUrl, m_typeNode)
				.changeAttribute(xml.ATTR_NAME, xml.ATTR_STRING, name)
				.changeAttribute("SourceFile", xml.ATTR_URL, fileUrlList[name]);
		}

		this.inherited(arguments);
	},
});

export = NewCompanyCustomFont;